import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { BASE_API_URL } from '@/lib/constants';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/**
 * Reset password page.
 * URL: /auth/reset-password/:id/:token
 * Patches new password via PATCH /Companies/:id with Authorization token.
 */
export default function ResetPassword() {
  const { id, token } = useParams<{ id: string; token: string }>();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Use direct axios with the reset token as Authorization header
      await axios.patch(
        `${BASE_API_URL}/Companies/${id}`,
        { password },
        { headers: { Authorization: token } }
      );
      setSuccess(true);
    } catch {
      setError('Error al cambiar la contraseña. El enlace puede haber expirado.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 h-full">
      <div className="flex content-center items-center justify-center h-full">
        <div className="w-full lg:w-4/12 px-4">
          <Card variant="legacy" className="mb-6">
            <div className="rounded-t mb-0 px-6 py-6">
              <div className="text-center mb-3">
                <h6 className="text-slate-500 text-sm font-bold">
                  Nueva contraseña
                </h6>
              </div>
              <hr className="mt-6 border-b-1 border-slate-300" />
            </div>
            <div className="flex-auto px-4 lg:px-10 py-10 pt-0">
              {success ? (
                <div className="text-center">
                  <svg className="mx-auto h-12 w-12 text-green-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="text-slate-600 mb-4">
                    Tu contraseña ha sido actualizada correctamente.
                  </p>
                  <Link
                    to="/auth/login"
                    className="bg-slate-800 text-white text-sm font-bold uppercase px-6 py-3 rounded shadow hover:shadow-lg inline-block"
                  >
                    Iniciar sesión
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="relative w-full mb-3">
                    <Label htmlFor="password">Nueva contraseña</Label>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                      required
                    />
                  </div>
                  <div className="relative w-full mb-4">
                    <Label htmlFor="confirmPassword">Confirmar contraseña</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      placeholder="Repite tu contraseña"
                      required
                    />
                  </div>

                  {error && (
                    <p className="text-red-500 text-sm text-center mb-4">{error}</p>
                  )}

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-slate-800 hover:bg-slate-700"
                  >
                    {isSubmitting ? 'Guardando...' : 'Cambiar contraseña'}
                  </Button>
                </form>
              )}
            </div>
          </Card>
          <div className="text-center">
            <Link to="/auth/login" className="text-slate-200">
              <small>Volver a iniciar sesión</small>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
